import React, { useState, useEffect, useRef } from 'react';
import {
  ComposedChart, AreaChart, Area, BarChart, Bar, Line, XAxis, YAxis,
  CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine, Cell, Legend
} from 'recharts';

function useAnimatedNumber(target, duration = 900) {
  const [display, setDisplay] = useState(target);
  const ref = useRef({ start: target, startTime: null, raf: null });
  useEffect(() => {
    const from = ref.current.start;
    ref.current.startTime = null;
    if (ref.current.raf) cancelAnimationFrame(ref.current.raf);
    function step(ts) {
      if (!ref.current.startTime) ref.current.startTime = ts;
      const pct = Math.min((ts - ref.current.startTime) / duration, 1);
      const ease = 1 - Math.pow(1 - pct, 3);
      setDisplay(from + (target - from) * ease);
      if (pct < 1) ref.current.raf = requestAnimationFrame(step);
      else { ref.current.start = target; setDisplay(target); }
    }
    ref.current.raf = requestAnimationFrame(step);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    return () => cancelAnimationFrame(ref.current.raf);
  }, [target, duration]);
  return display;
}

function gradeForTrade(trade) {
  const RULES_LIST = ['plan','sizing','stop loss','trend','setup','revenge','risk/reward','overtrading'];
  const rules = trade.rules_followed || trade.rules || '';
  const rulesStr = Array.isArray(rules) ? rules.join(' ') : String(rules);
  const matched = RULES_LIST.filter(r => rulesStr.toLowerCase().includes(r)).length;
  let score = matched * 10;
  const pnl = parseFloat(trade.pnl) || 0;
  if (pnl > 0) score += 20;
  const rr = parseFloat(trade.rr) || 0;
  if (rr >= 2) score += 10; else if (rr >= 1) score += 5;
  if (score >= 80) return { grade: 'A', color: '#0ecb81' };
  if (score >= 60) return { grade: 'B', color: '#2f8cff' };
  if (score >= 40) return { grade: 'C', color: '#f59e0b' };
  return { grade: 'D', color: '#ef4444' };
}

function AnimatedPnl({ value, isUp, greenBright, redBright }) {
  const animated = useAnimatedNumber(value);
  return (
    <div style={{ color: isUp ? greenBright : redBright, fontSize: 34, fontWeight: 900, letterSpacing: '-1px', lineHeight: 1.1 }}>
      {animated >= 0 ? '+' : ''}${Math.abs(animated).toFixed(2)}
    </div>
  );
}

function ArcGauge({ pct, size = 84, thickness = 10, label, sublabel, green, red }) {
  const cx = size / 2, cy = size / 2, r = (size - thickness) / 2;
  const circ = 2 * Math.PI * r;
  const clampedPct = Math.min(Math.max(pct, 0), 100);
  const greenArc = (clampedPct / 100) * circ;
  const redArc = circ - greenArc;
  const labelColor = clampedPct >= 50 ? green : red;
  return (
    <div style={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
      <svg width={size} height={size}>
        <circle cx={cx} cy={cy} r={r} fill="none" stroke="rgba(255,255,255,0.07)" strokeWidth={thickness} />
        <circle cx={cx} cy={cy} r={r} fill="none" stroke={red} strokeOpacity={0.8} strokeWidth={thickness}
          strokeDasharray={`${redArc} ${circ}`} strokeDashoffset={-greenArc}
          strokeLinecap="round" transform={`rotate(-90 ${cx} ${cy})`} />
        <circle cx={cx} cy={cy} r={r} fill="none" stroke={green} strokeWidth={thickness}
          strokeDasharray={`${greenArc} ${circ}`} strokeLinecap="round"
          transform={`rotate(-90 ${cx} ${cy})`} />
      </svg>
      <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
        <span style={{ color: labelColor, fontSize: 13, fontWeight: 800, lineHeight: 1 }}>{label}</span>
        {sublabel && <span style={{ color: 'rgba(149,165,192,0.7)', fontSize: 9, fontWeight: 600, letterSpacing: '0.5px' }}>{sublabel}</span>}
      </div>
    </div>
  );
}

export default function DashboardPanel({
  accountTrades, totalPnl, winRate, profitFactor,
  avgWin, avgLoss, wins, losses, breakEven,
  setActiveTab, themeMode, C, cardStyle, DonutChart, Sparkline, AdvancedCharts,
}) {
  const [equityRange, setEquityRange] = useState('all');
  const toNum = v => { const n = parseFloat(v); return isFinite(n) ? n : 0; };

  const calcIBKRCommission = (trade) => {
    const size = toNum(trade.size);
    const entry = toNum(trade.entry);
    const market = (trade.market || 'Stocks').toLowerCase();
    if (size <= 0 || entry <= 0) return 1.00;
    const tradeValue = size * entry;
    if (market === 'options') return Math.max(size * 0.65, 1.00);
    if (market === 'futures') return Math.max(size * 0.85, 0.85);
    if (market === 'forex') return Math.max(tradeValue * 0.00002, 2.00);
    if (market === 'crypto') return tradeValue * 0.0018;
    // Stocks (default): $0.005/share, min $1, max 1% of trade value
    return Math.min(Math.max(size * 0.005, 1.00), tradeValue * 0.01);
  };

  const green = '#0ecb81', greenBright = '#39e5a5', red = '#ef4444', redBright = '#f87171';

  // Equity curve + drawdown
  let running = 0, peak = 0;
  const equityData = accountTrades.map((t, i) => {
    running += toNum(t.pnl);
    if (running > peak) peak = running;
    const dd = peak > 0 ? parseFloat((running - peak).toFixed(2)) : 0;
    return { i: i + 1, equity: parseFloat(running.toFixed(2)), drawdown: dd };
  });
  const rangeMap = { '20': -20, '50': -50, '100': -100 };
  const sliced = equityRange in rangeMap ? equityData.slice(rangeMap[equityRange]) : equityData;

  // Stats
  const totalGrossWin = wins.reduce((s, t) => s + toNum(t.pnl), 0);
  const totalGrossLoss = Math.abs(losses.reduce((s, t) => s + toNum(t.pnl), 0));
  const avgRR = (losses.length && wins.length && totalGrossLoss > 0)
    ? (totalGrossWin / wins.length) / (totalGrossLoss / losses.length) : 0;
  const avgRRPct = Math.min((avgRR / 3) * 100, 100);
  const expectancy = accountTrades.length ? totalPnl / accountTrades.length : 0;
  const storedCommission = accountTrades.reduce((s, t) => s + toNum(t.commission), 0);
  const tradesWithoutComm = accountTrades.filter(t => toNum(t.commission) === 0);
  const ibkrEstimate = tradesWithoutComm.reduce((s, t) => s + calcIBKRCommission(t), 0);
  const totalCommission = storedCommission + ibkrEstimate;
  const commIsEstimated = tradesWithoutComm.length > 0;

  // Max drawdown %
  let eq3 = 0, pk3 = 0, maxDDPct = 0;
  accountTrades.forEach(t => {
    eq3 += toNum(t.pnl);
    if (eq3 > pk3) pk3 = eq3;
    if (pk3 > 0) { const pct = ((pk3 - eq3) / pk3) * 100; if (pct > maxDDPct) maxDDPct = pct; }
  });

  // Sharpe ratio (annualised, using daily returns, risk-free=0)
  const dailyMap = {};
  accountTrades.forEach(t => {
    if (!t.date) return;
    dailyMap[t.date] = (dailyMap[t.date] || 0) + toNum(t.pnl);
  });
  const dailyReturns = Object.values(dailyMap);
  const meanReturn = dailyReturns.length ? dailyReturns.reduce((a, b) => a + b, 0) / dailyReturns.length : 0;
  const stdDev = dailyReturns.length > 1
    ? Math.sqrt(dailyReturns.reduce((s, r) => s + Math.pow(r - meanReturn, 2), 0) / (dailyReturns.length - 1))
    : 0;
  const sharpe = stdDev > 0 ? parseFloat(((meanReturn / stdDev) * Math.sqrt(252)).toFixed(2)) : 0;

  // Monthly P&L
  const monthlyMap = {};
  accountTrades.forEach(t => {
    if (!t.date) return;
    const d = new Date(t.date);
    if (isNaN(d.getTime())) return;
    const key = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`;
    monthlyMap[key] = (monthlyMap[key] || 0) + toNum(t.pnl);
  });
  const monthlyRows = Object.entries(monthlyMap)
    .sort(([a],[b]) => a.localeCompare(b))
    .map(([key, pnl]) => {
      const [yr, mo] = key.split('-');
      const label = new Date(+yr, +mo-1, 1).toLocaleString('default', { month: 'short', year: '2-digit' });
      return { label, pnl: parseFloat(pnl.toFixed(2)) };
    });

  const dailyBars = Object.entries(dailyMap)
    .map(([date, pnl]) => {
      const d = new Date(date);
      const label = isNaN(d.getTime()) ? date.slice(0, 5) : `${d.getMonth()+1}/${d.getDate()}`;
      return { date: label, pnl: parseFloat(pnl.toFixed(2)) };
    }).slice(-20);

  const recent = [...accountTrades].slice(-5).reverse();
  const isUp = totalPnl >= 0;
  const gradId = 'dashGrad';
  const labelStyle = { color: C.sub, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: 8 };

  return (
    <div style={{ fontFamily: C.font, paddingBottom: 28 }}>

      {/* TOP BAR */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ color: C.sub, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px' }}>Portfolio Overview</div>
        <div style={{ color: C.text, fontSize: 22, fontWeight: 800, marginTop: 2 }}>{accountTrades.length} Total Trades</div>
      </div>

      {/* TOP 3 KEY STATS */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 12 }}>

        {/* Net PNL */}
        <div style={{ ...cardStyle, padding: '22px 24px' }}>
          <div style={labelStyle}>Net P&L</div>
          <AnimatedPnl value={totalPnl} isUp={isUp} greenBright={greenBright} redBright={redBright} />
          <div style={{ color: C.sub, fontSize: 11, marginTop: 6 }}>{accountTrades.length} closed trade{accountTrades.length !== 1 ? 's' : ''}</div>
        </div>

        {/* Win Rate */}
        <div style={{ ...cardStyle, padding: '22px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={labelStyle}>Win Rate</div>
            <div style={{ color: C.text, fontSize: 34, fontWeight: 900, letterSpacing: '-0.5px', lineHeight: 1.1 }}>
              {winRate.toFixed(1)}<span style={{ fontSize: 18, fontWeight: 600 }}>%</span>
            </div>
            <div style={{ color: C.sub, fontSize: 11, marginTop: 6 }}>{wins.length}W · {losses.length}L · {breakEven.length}BE</div>
          </div>
          <ArcGauge pct={winRate} green={green} red={red} label={`${winRate.toFixed(0)}%`} />
        </div>

        {/* Profit Factor */}
        <div style={{ ...cardStyle, padding: '22px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={labelStyle}>Profit Factor</div>
            <div style={{ color: parseFloat(profitFactor) >= 1 ? greenBright : redBright, fontSize: 34, fontWeight: 900, letterSpacing: '-0.5px', lineHeight: 1.1 }}>
              {profitFactor}
            </div>
            <div style={{ color: C.sub, fontSize: 11, marginTop: 6 }}>Gross win ÷ gross loss</div>
          </div>
          <ArcGauge pct={Math.min(parseFloat(profitFactor) / 3 * 100, 100)} green={green} red={red} label={profitFactor} sublabel="PF" />
        </div>
      </div>

      {/* SECONDARY STATS ROW */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12, marginBottom: 16 }}>
        <div style={{ ...cardStyle, padding: '14px 20px' }}>
          <div style={labelStyle}>Avg RR</div>
          <div style={{ color: avgRR >= 1 ? greenBright : redBright, fontSize: 22, fontWeight: 800 }}>{avgRR.toFixed(2)}</div>
          <div style={{ color: C.sub, fontSize: 10, marginTop: 3 }}>Risk / Reward ratio</div>
        </div>
        <div style={{ ...cardStyle, padding: '14px 20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
            <div style={labelStyle}>Total Commission</div>
            {commIsEstimated && (
              <span style={{ fontSize: 9, fontWeight: 700, background: 'rgba(245,158,11,0.15)', color: '#f59e0b', border: '1px solid rgba(245,158,11,0.3)', borderRadius: 4, padding: '1px 5px', letterSpacing: '0.3px' }}>IBKR EST</span>
            )}
          </div>
          <div style={{ color: totalCommission > 0 ? redBright : C.sub, fontSize: 22, fontWeight: 800 }}>
            -${totalCommission.toFixed(2)}
          </div>
          <div style={{ color: C.sub, fontSize: 10, marginTop: 3 }}>
            {commIsEstimated
              ? `$${storedCommission.toFixed(2)} logged · $${ibkrEstimate.toFixed(2)} IBKR est (${tradesWithoutComm.length} trade${tradesWithoutComm.length !== 1 ? 's' : ''})`
              : 'Fees paid to broker'}
          </div>
        </div>
      </div>

      {/* CHARTS */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>

        {/* Equity Curve */}
        <div style={{ ...cardStyle, padding: '20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
            <div>
              <div style={{ color: C.sub, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px' }}>Equity Curve</div>
              <div style={{ color: isUp ? greenBright : redBright, fontSize: 14, fontWeight: 700, marginTop: 2 }}>{isUp ? '+' : ''}${totalPnl.toFixed(2)}</div>
            </div>
            <div style={{ display: 'flex', gap: 4 }}>
              {[['all', 'All'], ['100', '100'], ['50', '50'], ['20', '20']].map(([v, l]) => (
                <button key={v} onClick={() => setEquityRange(v)} style={{
                  padding: '3px 8px', fontSize: 10, fontWeight: 700, borderRadius: 5,
                  border: `1px solid ${equityRange === v ? C.blue : C.border}`,
                  background: equityRange === v ? `${C.blue}22` : 'transparent',
                  color: equityRange === v ? C.blueBright : C.sub, cursor: 'pointer',
                }}>{l}</button>
              ))}
            </div>
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <ComposedChart data={sliced}>
              <defs>
                <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={isUp ? green : red} stopOpacity={0.25} />
                  <stop offset="95%" stopColor={isUp ? green : red} stopOpacity={0} />
                </linearGradient>
                <linearGradient id="ddGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={red} stopOpacity={0.35} />
                  <stop offset="95%" stopColor={red} stopOpacity={0.05} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
              <XAxis dataKey="i" stroke={C.sub} tick={{ fontSize: 9 }} tickFormatter={v => `T${v}`} interval="preserveStartEnd" />
              <YAxis stroke={C.sub} tick={{ fontSize: 9 }} tickFormatter={v => `$${v}`} width={55} />
              <Tooltip
                contentStyle={{ background: C.card2, border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 11, color: C.text }}
                labelStyle={{ color: C.text }} itemStyle={{ color: C.text }}
                formatter={(v, name) => [`$${v}`, name === 'equity' ? 'Equity' : 'Drawdown']}
                labelFormatter={l => `Trade ${l}`}
              />
              <ReferenceLine y={0} stroke={C.border} strokeDasharray="4 4" />
              <Area type="monotone" dataKey="equity" stroke={isUp ? green : red} strokeWidth={2.5} fill={`url(#${gradId})`} dot={false} />
              <Area type="monotone" dataKey="drawdown" stroke={red} strokeWidth={1.5} fill="url(#ddGrad)" dot={false} strokeDasharray="3 2" />
            </ComposedChart>
          </ResponsiveContainer>
        </div>

        {/* Daily PNL */}
        <div style={{ ...cardStyle, padding: '20px' }}>
          <div style={{ color: C.sub, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px', marginBottom: 14 }}>Daily P&L</div>
          <ResponsiveContainer width="100%" height={195}>
            <BarChart data={dailyBars}>
              <CartesianGrid strokeDasharray="3 3" stroke={C.border} vertical={false} />
              <XAxis dataKey="date" stroke={C.sub} tick={{ fontSize: 8 }} />
              <YAxis stroke={C.sub} tick={{ fontSize: 9 }} tickFormatter={v => `$${v}`} width={55} />
              <Tooltip contentStyle={{ background: C.card2, border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 11, color: C.text }} labelStyle={{ color: C.text }} itemStyle={{ color: C.text }} formatter={v => [`$${v}`, 'P&L']} />
              <ReferenceLine y={0} stroke={C.border} />
              <Bar dataKey="pnl" maxBarSize={28} radius={[4, 4, 0, 0]}>
                {dailyBars.map((e, i) => <Cell key={i} fill={e.pnl >= 0 ? green : red} fillOpacity={0.88} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

      </div>

      {/* MONTHLY P&L TABLE */}
      {monthlyRows.length > 0 && (
        <div style={{ ...cardStyle, padding: '20px', marginTop: 14 }}>
          <div style={{ color: C.sub, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px', marginBottom: 16 }}>Monthly P&L Breakdown</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))', gap: 10 }}>
            {monthlyRows.map(({ label, pnl }) => (
              <div key={label} style={{ background: pnl >= 0 ? `${green}12` : `${red}12`, border: `1px solid ${pnl >= 0 ? green : red}33`, borderRadius: 10, padding: '12px 14px', textAlign: 'center' }}>
                <div style={{ color: C.sub, fontSize: 10, fontWeight: 700, marginBottom: 6 }}>{label}</div>
                <div style={{ color: pnl >= 0 ? greenBright : redBright, fontSize: 15, fontWeight: 800 }}>
                  {pnl >= 0 ? '+' : ''}${pnl.toFixed(2)}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
